import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from 'gw-digital-auth-react';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { InputField, CheckboxField, DropdownSelectField, ModalNextProvider, Loader } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import {ViewModelServiceContext} from 'gw-portals-viewmodel-react'
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useValidation } from 'gw-portals-validation-react';

import metadata from './FarmerDetailsPage.metadata.json5';
import messages from 'gw-capability-quoteandbind-farm-react/pages/FarmerDetails/FarmerDetailsPage.messages';
import styles from './FarmerDetailsPage.module.scss';
import { messages as commonMessages } from 'gw-platform-translations';
import { useDependencies } from 'gw-portals-dependency-react';
import { ClausesUtil } from 'gw-policycommon-util-js';
import SearchContactComponent from 'gw-capability-policychange-ho-react/components/SearchContact/SearchContactComponent';
import ContactComponent from 'gw-capability-policychange-ho-react/components/Contact/ContactComponent';
import PortalUtil from '../../../../utils/PortalUtil';
import { ContactService } from 'gw-capability-gateway';

function FarmerDetailsPage(props) {
    const translator = useContext(TranslatorContext);
    const { wizardData, updateWizardData, currentStepIndex, changeNextSteps} = props;
    const { submissionVM, selectedData } = wizardData;
    const viewModelService = useContext(ViewModelServiceContext);
    const [isPageInitialized, setPageInitialized] = useState(false);
    const { isComponentValid, initialValidation, onValidate,registerComponentValidation } = useValidation('FarmerDetailsPage');
    const [showLoader, setShowLoader] = useState(false);
    const { SpreadsheetService } = useDependencies('SpreadsheetService');
    const { DocumentService } = useDependencies('DocumentService');
    const [underwritingIssues, setUnderwritingIssues] = useState([]);
    const [errorsAndWarnings, setErrorsAndWarnings] = useState([]);
    const filterUWIssuesInCustomOffering = true;
    const [accountContacts, setAccountContacts] = useState([{}]);
    const [editContactVM, setEditContactVM] = useState({});
    const [editContactIndex, setEditContactIndex] = useState(-1)
    const [newContactVM, setNewContactVM] = useState({});
    const [newContactInsuredInterestVM, setNewContactInsuredInterestVM] = useState({});
    const [searchInsuredInterest, setSearchInsuredInterest] = useState(false);
    const [addNewInsuredInterest, setAddNewInsuredInterest] = useState(false);
    const [farmerInsuredPath, setFarmerInsuredPath] = useState('');
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader } = useAuthentication();
    const [isLoading, updateIsLoading] = useState(false);
    const {dateUtil} = PortalUtil;

    const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
    const BLOCKS_BIND = 'BlocksBind';
    const NON_BLOCKING = 'NonBlocking';
    const isDetailsComponentValid = useCallback(() => {
        return true;
    }, [submissionVM]);
    const [loadingClause, updateLoadingClause] = useState();

    useEffect(() => {
        setPageInitialized(true);
        registerComponentValidation(isDetailsComponentValid);
        // The above action only need to run once when the page is mounted
        // eslint-disable-next-line react-hooks/exhaustive-deps

        const _contacts = submissionVM.baseData.account.accountContacts.value.map((c) => {
                c['id'] = c.publicID;
                return c;
          });
        _contacts.push({ id: "placeholder", displayName: "Search Existing Contacts" });
        setAccountContacts(_contacts);
    }, [registerComponentValidation, isDetailsComponentValid]);

    /*const onValueChange = useCallback((value, item, prop) => {
        const index = submissionVM.lobData.homeowners.allCoverables.value.indexOf(item.value);
        const path = `lobData.homeowners.allCoverables.children.${index}.${prop.dataPath}`;
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.set(newSubmissionVM, path, value);
        if(prop.dataPath === 'isPrimary') {
            newSubmissionVM.lobData.homeowners.allCoverables.children.forEach(element => {
                if(element._accessorCode != item._accessorCode) {
                    _.set(element, 'isPrimary', false);
                }
            });
        }
        updateWizardData(wizardData);
        }, [submissionVM]
    );*/

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData]
    );

    const onUpdateCustomQuote = useCallback(
        () => {
            updateIsLoading(true);
            return EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            ).then((updatedSubmission) => {
                submissionVM.value = updatedSubmission;
                updateIsLoading(false);
                updateWizardData(wizardData);
                updateLoadingClause(undefined);
            });
        },
        [
            authHeader,
            submissionVM,
            updateWizardData,
            viewModelService,
            EndorsementService
        ]
    );

    const onClauseChange = useCallback(
        (schedule, path) => {
            writeValue(schedule, path);
            return onUpdateCustomQuote();
        },
        [onUpdateCustomQuote, writeValue]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            _.set(submissionVM, changedPath, value);
            ClausesUtil.setClauseValue(submissionVM, value, changedPath);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData]
    );

    const getLoadingClause = useCallback((path, baseObject) => {
        const publicID = baseObject.coveragePublicID || baseObject.publicID;
        const parentCoveragePath = ClausesUtil.hasSubCoverages(path)
            ? ClausesUtil.getParentCoveragePath(path)
            : undefined;

        if (parentCoveragePath) {
            const vehicleCoverages = _.get(
                submissionVM,
                'lobData.businessAuto.offerings.children[0].coverages.vehicleCoverages'
            );
            const vehicleCoverage = _.get(
                submissionVM,
                parentCoveragePath
            );
            return {
                index: vehicleCoverages.value.findIndex(
                    (section) => section === vehicleCoverage.value
                ),
                publicID: publicID
            };
        }

        return publicID;
    }, [submissionVM]);

    const syncClauses = useCallback(
        async (value, changedPath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            const baseObject = _.get(submissionVM, basePath);
            updateLoadingClause(getLoadingClause(basePath, baseObject));
            await onClauseChange();
            updateWizardData(wizardData);
        },
        [onClauseChange, submissionVM, updateWizardData, getLoadingClause]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath) => {
            changeSubmission(value, changedPath);
            syncClauses(value, changedPath);
        },
        [changeSubmission, syncClauses]
    );

    const handleError = useCallback(
        (title, message) => {
            return ModalNextProvider.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    const onAddFarmDetails = useCallback( async () => {
        const farmerInsuredDetails = _.get(
            submissionVM,
            'lobData.farm.farmerPolicyCoverages.farmerInsured'
        );
        farmerInsuredDetails.value = farmerInsuredDetails.value || [];
        const { _xCenter, _dtoName } = farmerInsuredDetails;
        const farmerVM = viewModelService.create({}, _xCenter, _dtoName);
        farmerInsuredDetails.pushElement(farmerVM);
        updateIsLoading(true);
        submissionVM.value = await EndorsementService.saveEndorsement(
            [submissionVM.value],
            authHeader
        );
        updateIsLoading(false);
        updateWizardData(wizardData);
    }, [submissionVM, updateWizardData, viewModelService, EndorsementService]);

    const removeFarmDetails = useCallback(
        async (evt) => {
            const farmerInsuredDetails = _.get(
                submissionVM,
                evt.path
            );
            updateIsLoading(true);
            farmerInsuredDetails.isDelete.value = true;
            submissionVM.value = await EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            );
            updateIsLoading(false);
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData, viewModelService, EndorsementService]
    );

    const findBlockingPoint = (type) => {
        return underwritingIssues.some((offeringIssue) => {
            if (type === BLOCKS_BIND) {
                return offeringIssue.currentBlockingPoint === BLOCKS_BIND;
            }
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint);
        });
    };

    const farmerInsuredObjects = _.get(
        submissionVM,
        'lobData.farm.farmerPolicyCoverages.farmerInsured.value'
    );

    const onEditValueChange = (value, path, index) => {
        var _editContactVM = viewModelService.clone(editContactVM);
        _.set(_editContactVM, path, value);
        setEditContactVM(_editContactVM);
    }

    const onCancelEditContact = () => {
        setEditContactVM({});
        setEditContactIndex(-1);
    }

    const onSaveEditContact = (index) => {
        //var _editContactVM = viewModelService.clone(editContactVM); 
        updateIsLoading(true);       
        const accountId = submissionVM.baseData.accountNumber.value;
        ContactService.updateAccountContact(accountId, editContactVM.value, authHeader).then((updatedContact) =>{
            updateContacts(updatedContact);
            setEditContactVM({});
            setEditContactIndex(-1);
            updateIsLoading(false);
        }).finally(() => {
            setEditContactVM({});
            setEditContactIndex(-1);
            updateIsLoading(false);
        })
          
    }

    const handleAddNewClick = () => {
        const contactModel =  {
            subtype: "Person",
            primaryAddress: {
                country: "CA",
                state: "ON",
                isFromPortal: true
            },
            isFromPortal: true
        }
        setNewContactVM(createVM(contactModel));
        setSearchInsuredInterest(false);
        setAddNewInsuredInterest(true);
        setFarmerInsuredPath('');
    }

    const handleInsuredInterestCancel = () => {
        setSearchInsuredInterest(false);
        setAddNewInsuredInterest(false);
        setFarmerInsuredPath('');
    };

    const saveContact = useCallback(
         async (editContact) => {
            const data = editContact ? editContact.value : newContactVM.value
            data.displayName = data.firstName + ' ' + data.lastName;
            updateIsLoading(true);
            submissionVM.value = await EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            );
            updateIsLoading(false);
            setSearchInsuredInterest(false);
            setAddNewInsuredInterest(false);
            setFarmerInsuredPath('');
            updateWizardData(wizardData);
        },
        [submissionVM, updateWizardData, viewModelService, EndorsementService]
    );

    const cancelContact = () => {
        setSearchInsuredInterest(false);
        setAddNewInsuredInterest(false);
        setFarmerInsuredPath('');
    }

    const onNewValueChange = (value, path) => {
        var _newContactVM = viewModelService.clone(newContactVM);
        _.set(_newContactVM, path, value)
        setNewContactVM(_newContactVM);
    }

    const editAdditionalInsuredInterest = (index, type) => {
        setEditContactIndex(index);
        let publicId = '';
        if(type === 'farmerInsured'){
            publicId = submissionVM.lobData.farm.farmerPolicyCoverages.farmerInsured.value[index].farmerInsured;
        } else {
            publicId = submissionVM.lobData.farm.farmerPolicyCoverages.farmerInsured.value[index].beneficiary;
        }
        if(publicId && publicId !== ''){
            const accountContact = _.find(submissionVM.baseData.account.accountContacts.value, (contact) => {
                return contact.publicID === publicId;
            });
            const _contactVM = accountContact;
            _contactVM.isFromPortal = true;
            _contactVM.primaryAddress.isFromPortal = true;
            setEditContactVM(createVM(_contactVM));
        }
    }

    const deleteAdditionalInsuredInterest = (index, type) => {
        if(type === 'farmerInsured'){
            submissionVM.lobData.farm.farmerPolicyCoverages.farmerInsured.value[index].farmerInsured = '';
        } else {
            submissionVM.lobData.farm.farmerPolicyCoverages.farmerInsured.value[index].beneficiary = '';
        }
        updateWizardData(wizardData);
    }

    const createVM = (model) => {
        return viewModelService.create(
            model,
            'pc',
            'edge.capabilities.policycommon.accountcontact.dto.AccountContactDTO'
        );
    };

    const updateContacts = async (contact) => {
        updateIsLoading(true);
        const updatedAccountContacts = await EndorsementService.getAccountContacts(submissionVM.jobID.value, authHeader);
        //updateAccountContacts(updatedAccountContacts);
        //setAdditionalInsuredInterest(updatedAccountContacts, contact.publicID);
        updateIsLoading(false);
        submissionVM.baseData.account.accountContacts.value = updatedAccountContacts;
        const _contacts = updatedAccountContacts.map((c) => {
                c['id'] = c.publicID;
                return c;
          });
        _contacts.push({ id: "placeholder", displayName: "Search Existing Contacts" });
        setAccountContacts(_contacts);
        setSearchInsuredInterest(false);
        if(farmerInsuredPath){
            var famerInsBen = _.get(submissionVM,farmerInsuredPath)
            famerInsBen.value = contact.publicID;
        }        
        updateWizardData(wizardData);
    }

    const onAdditionalInsuredInterestChange = useCallback((value, model, props) => {
        if (value == "placeholder") {
            setSearchInsuredInterest(true);
            setFarmerInsuredPath(model);
        }
        else {
            var famerInsBen = _.get(submissionVM,model)
            famerInsBen.value = value;
            setSearchInsuredInterest(false);
            setFarmerInsuredPath('');
            updateWizardData(wizardData);
        }
    }, [updateWizardData]);

    const overrideFarmerProps = useCallback(() => {
        if(farmerInsuredObjects !== undefined ) {
        const overrideProps = farmerInsuredObjects.map((value, index) => {
            if(value.farmerInsured !== undefined){
                var insuredContact = _.find(accountContacts, (contact) => {
                        return contact.publicID === value.farmerInsured
                    });
            }
            if(value.beneficiary !== undefined){
                var beneficiaryContact = _.find(accountContacts, (contact) => {
                        return contact.publicID === value.beneficiary
                    });
            }
            return {
                [`faFarmerCount${index}`]: {
                    content: translator(messages.farmerCount, {
                        count: index + 1
                    })
                },
                [`farmerInsured${index}`]: {
                    availableValues: accountContacts,
                    onValueChange: onAdditionalInsuredInterestChange,
                    value: value.farmerInsured,
                },
                [`farmerInsuredContainer${index}`]: {
                    visible: !searchInsuredInterest && editContactIndex !== index && !addNewInsuredInterest
                },
                [`farmerBeneficiary${index}`]: {
                    availableValues: accountContacts,
                    onValueChange: onAdditionalInsuredInterestChange,
                    value: value.beneficiary,
                },
                [`farmerBeneficiaryContainer${index}`]: {
                    visible: !searchInsuredInterest && editContactIndex !== index && !addNewInsuredInterest
                },
                [`editContact${index}`]: {
                    contactVM: editContactVM,
                    onValueChange: (value, path) => { onEditValueChange(value, path, index) },
                    visible: editContactIndex === index
                },
                [`saveEditContact${index}`]: {
                    onClick: () => { onSaveEditContact(index) },
                    visible: editContactIndex === index,
                    disabled: editContactVM.aspects ? (!editContactVM.aspects.valid
                        || !editContactVM.aspects.subtreeValid) : false
                },
                [`cancelEditContact${index}`]: {
                    onClick: onCancelEditContact,
                    visible: editContactIndex === index
                },
                [`editAdditionalInsuredInterest${index}`]: {
                    onClick: (e) => { editAdditionalInsuredInterest(index, 'farmerInsured') },
                    disabled: Object.keys(editContactVM).length > 0 || value.farmerInsured === undefined || value.farmerInsured === ''
                },
                [`editBeneficiary${index}`]: {
                    onClick: (e) => { editAdditionalInsuredInterest(index, 'beneficiary') },
                    disabled: Object.keys(editContactVM).length > 0 || value.beneficiary === undefined || value.beneficiary === ''
                },
                [`deleteAdditionalInsuredInterest${index}`]: {
                    onClick: (e) => { deleteAdditionalInsuredInterest(index, 'farmerInsured') }
                },
                [`deleteBeneficiary${index}`]: {
                    onClick: (e) => { deleteAdditionalInsuredInterest(index, 'beneficiary') }
                },
                [`searchContact${index}`]: {
                    visible: searchInsuredInterest,
                    authHeader: authHeader,
                    accountNumber: submissionVM.baseData.account.accountNumber.value,
                    updateContacts: updateContacts,
                    handleAddNew: handleAddNewClick,
                    handleCancel: handleInsuredInterestCancel
                },
                [`addNewContainer${index}`]: {
                    visible: addNewInsuredInterest
                },
                [`addContact${index}`]: {
                    contactVM: newContactVM,
                    onValueChange: onNewValueChange
                },
                [`saveContactBtn${index}`]: {
                    onClick: saveContact,
                    disabled: newContactVM.aspects ? (!newContactVM.aspects.valid
                        || !newContactVM.aspects.subtreeValid) : false
                },
                [`cancelContactBtn${index}`]: {
                    onClick: cancelContact
                },
                [`insuredAge${index}`]: {
                    value: value.farmerInsured !== undefined  && insuredContact !== undefined && insuredContact.dateOfBirth !==undefined ? dateUtil.getAge(insuredContact.dateOfBirth) : "",
                    visible: value.farmerInsured !== undefined
                },
                [`insuredAddress${index}`]: {
                    visible: value.farmerInsured !== undefined,
                    value: value.farmerInsured !== undefined && insuredContact !== undefined  ? insuredContact.primaryAddress.displayName : ""
                },
                [`beneficiaryAge${index}`]: {
                    value: value.beneficiary !== undefined && beneficiaryContact !== undefined && beneficiaryContact.dateOfBirth !==undefined ? dateUtil.getAge(beneficiaryContact.dateOfBirth):"",
                    visible: value.beneficiary !== undefined
                },
                [`beneficiaryAddress${index}`]: {
                    visible: value.beneficiary !== undefined,
                    value: value.beneficiary !== undefined && beneficiaryContact !== undefined ? beneficiaryContact.primaryAddress.displayName : ""
                },
            };
        });

        return Object.assign({}, ...overrideProps);
    } else {return null}
    }, [farmerInsuredObjects, translator, editContactVM, editContactIndex, authHeader, searchInsuredInterest, addNewInsuredInterest, accountContacts, updateWizardData]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        /*underWritingIssues: {
            underwritingIssues: underwritingIssues,
            blockQuote: findBlockingPoint(),
            quoteID: submissionVM.quoteID.value,
            filterUWIssuesInCustomOffering
        },
        errorsAndWarningsTable: {
            data: errorsAndWarnings
        },
        processInlineLoader: {
            loaded: !showLoader,
            loadingMessage: 'Please wait while we upload the document.'
        },
        PackageDetailsUploadPage: {
            visible: !showLoader
        },*/
        farmerDetailsContainer: {
            visible: submissionVM.baseData.isFarmLineExist.value === true
        },
        ...overrideFarmerProps()
    };

    const onNext = useCallback(async () => {
        submissionVM.value = await EndorsementService.saveEndorsement(
            [submissionVM.value],
            authHeader
        );

        submissionVM.value = await EndorsementService.quoteEndorsement(
            [submissionVM.jobID.value],
            authHeader
        );

        return wizardData;
    }, [submissionVM, EndorsementService, authHeader, wizardData]);

    const resolvers = {
        resolveCallbackMap: {
            onAddFarmDetails: onAddFarmDetails,
            onRemoveFarmDetails: removeFarmDetails,
            onChangeClause: changeSubmission,
            onSyncCoverages: syncClauses,
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onValidate: onValidate
        },
        resolveComponentMap: {
            searchContactComponent: SearchContactComponent,
            contactComponent: ContactComponent
        },
    };

    if (!isPageInitialized) {
        return null;
    }


    return isLoading ? (
          <Loader loaded={!isLoading} />
      ) :  (
        <WizardPage onNext={onNext} disableNext={!isComponentValid}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

FarmerDetailsPage.propTypes = wizardProps;
export default FarmerDetailsPage;
